import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api'
import type {
  ListaPlanificaciones,
  PanelPlanificacion,
  PlanificacionEntrada,
} from '@/lib/schemas/planificacion'

const jsonInit = (metodo: 'POST' | 'PUT', cuerpo?: unknown): RequestInit => ({
  method: metodo,
  ...(cuerpo === undefined
    ? {}
    : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuerpo) }),
})

export function usePlanificaciones(proyectoId: string, mostrarDeBaja: boolean) {
  const api = useApi()
  const params = new URLSearchParams({ proyecto: proyectoId })
  if (mostrarDeBaja) params.set('baja', '1')
  return useQuery({
    queryKey: ['planificaciones', proyectoId, mostrarDeBaja],
    queryFn: () => api<ListaPlanificaciones>(`/api/planificaciones?${params.toString()}`),
    enabled: Boolean(proyectoId),
    placeholderData: (previa) => previa,
  })
}

export function usePanelPlanificacion(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['planificacion', id],
    queryFn: () => api<PanelPlanificacion>(`/api/planificaciones/${id ?? ''}`),
    enabled: Boolean(id),
  })
}

function useInvalidar() {
  const qc = useQueryClient()
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['planificaciones'] }),
      qc.invalidateQueries({ queryKey: ['planificacion'] }),
    ])
  }
}

export function useGuardarPlanificacion() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: PlanificacionEntrada }) =>
      id
        ? api<null>(`/api/planificaciones/${id}`, jsonInit('PUT', datos))
        : api<{ id: string }>('/api/planificaciones', jsonInit('POST', datos)),
    onSuccess: invalidar,
  })
}

type Accion =
  | 'enviar'
  | 'corregida'
  | 'baja'
  | 'restaurar'
  | 'aprobar'
  | 'cambios'
  | 'deshacer'
  | 'visita'

export function useAccionPlanificacion() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: Accion }) =>
      api<null>(`/api/planificaciones/${id}/${accion}`, jsonInit('POST')),
    onSuccess: invalidar,
  })
}

export function useComentarPlanificacion(id: string) {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (texto: string) =>
      api<null>(`/api/planificaciones/${id}/comentarios`, jsonInit('POST', { texto })),
    onSuccess: invalidar,
  })
}

export function useResolverComentarioPlan() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({
      comentarioId,
      resuelto,
    }: {
      comentarioId: string
      resuelto: boolean
    }) =>
      api<null>(
        `/api/planificaciones/comentarios/${comentarioId}/resuelto`,
        jsonInit('POST', { resuelto }),
      ),
    onSuccess: invalidar,
  })
}

/** ¿El documento lo ve cualquiera con el enlace? `null`: no se pudo saber. */
export function useDocumentoPublico(url: string) {
  const api = useApi()
  const valida = /^https:\/\/docs\.google\.com\/.+\/d\/[\w-]{20,}/.test(url)
  return useQuery({
    queryKey: ['doc-publico', url],
    queryFn: () =>
      api<{ publico: boolean | null }>(
        `/api/planificaciones/doc-publico?url=${encodeURIComponent(url)}`,
      ),
    enabled: valida,
    staleTime: 60_000,
  })
}
